import React, {useRef, forwardRef} from 'react'
import * as Tabs from '@radix-ui/react-tabs'
import {TabsProps, TabsTriggerProps, TabsListProps, TabsContentProps} from './types'
import {useRefObjectAsForwardedRef} from '../../hooks'

/**
 * A set of layered sections of content—known as tab panels—that are displayed one at a time.
 */

/**
 * Contains all the tabs component parts.
 */
export const Root = forwardRef(({children, ...props}: TabsProps, forwardedRef) => {
  const innerRef = React.useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <Tabs.Root ref={innerRef} {...props}>
      {children}
    </Tabs.Root>
  )
})

/**
 *
 * Contains the triggers that are aligned along the edge of the active content.
 *
 */
export const List = forwardRef(({children, ...props}: TabsListProps, forwardedRef) => {
  const innerRef = React.useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <Tabs.List ref={innerRef} {...props}>
      {children}
    </Tabs.List>
  )
})

/**
 * The button that activates its associated content.
 */
export const Trigger = forwardRef(({children, ...props}: TabsTriggerProps, forwardedRef) => {
  const innerRef = React.useRef<HTMLButtonElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <Tabs.Trigger ref={innerRef} {...props}>
      {children}
    </Tabs.Trigger>
  )
})

/**
 * Contains the content associated with each trigger.
 */
export const Content = forwardRef(({children, ...props}: TabsContentProps, forwardedRef) => {
  const innerRef = React.useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <Tabs.Content ref={innerRef} {...props}>
      {children}
    </Tabs.Content>
  )
})

// Exports
Root.displayName = 'Tabs'
Trigger.displayName = 'Trigger'
Content.displayName = 'TContent'
List.displayName = 'List'

/**
 * Provides a list of tabs that a user can select from to switch between multiple tab panels.
 */
export default Object.assign(Root, {
  Trigger,
  Content,
  List
})
